export const API_ENDPOINTS = {
  ALERTS: {
    CREATE_ALERT: (reportId: number) => `/transmission-reports/${reportId}/alerts`,
    UPDATE_ALERT: (reportId: number, alertId: string) =>
      `/transmission-reports/${reportId}/alerts/${alertId}`,
    DELETE_ALERT: (reportId: number, alertId: string) =>
      `/transmission-reports/${reportId}/alerts/${alertId}`,
    UPLOAD_IMAGE: (reportId: number, alertId: string) =>
      `/transmission-reports/${reportId}/alerts/${alertId}/image`,
    DELETE_IMAGE: (reportId: number, alertId: string, imageId: string) =>
      `/transmission-reports/${reportId}/alerts/${alertId}/image/${imageId}`
  },
  BILLING: {
    FIND_BY_REPORT_ID: (reportId: number) => `/transmission-reports/${reportId}/billing`,
    UPDATE_BILLING: (billingId: string) => `/billing/${billingId}`,
    UPDATE_IS_BILLABLE: (billingId: string) => `/billing/${billingId}/billable`,
    ADD_DIAGNOSIS_CODE: (reportId: number, billingId: string) =>
      `/transmission-reports/${reportId}/billing/${billingId}/addDiagnosisCode`
  },
  CLINICAL_NOTES: {
    CREATE: `/clinical-notes`,
    UPDATE: (noteId: string) => `/clinical-notes/${noteId}`,
    DELETE: (noteId: string) => `/clinical-notes/${noteId}`,
    ARCHIVE: (noteId: string) => `/clinical-notes/${noteId}/archive`,
    UNARCHIVE: (noteId: string) => `/clinical-notes/${noteId}/unarchive`,
    GET_NOTES_BY_PATIENT_ID: (patientId: string) => `/clinical-notes/patient/${patientId}`,
    GET_PINNED_NOTES: (patientId: string) => `/clinical-notes/patient/pinned/${patientId}`
  },
  COUNTERS: {
    CREATE: (reportId: number) => `/transmission-reports/${reportId}/counters`,
    UPDATE: (reportId: number) => `/transmission-reports/${reportId}/updateCounters`
  },
  FILES: {
    ROOT: '/files',
    UPLOAD: '/files/upload'
  },
  NOTES: {
    UPDATE_MULTIPLE: (reportId: number) => `/transmission-reports/${reportId}/notes/multiple`
  },
  ORGANIZATIONS: {
    FIND_BY_ID: (organizationId: string) => `/organizations/${organizationId}`,
    SETTINGS_FULL: (organizationId: string) => `/organizations/${organizationId}/settings/full`
  },
  PATIENTS: {
    LIST: '/patients',
    DEVICELEAD: '/device-lead',
    LIST_V2: '/patients/v2/list',
    SETTINGS: '/patients/v2/patient-settings',
    TOTAL_MONITORED: '/patients/total-monitored',
    FILTERED_TOTAL_MONITORED: '/patients/filtered-total-monitored',
    AUTHORIZED_PRACTITIONERS: (patientId: string) =>
      `/patients/${patientId}/authorized-practitioners`,
    IN_CLINIC_PRACTITIONERS: (patientId: string) => `/patients/${patientId}/inclinic-practitioners`,
    UPDATE_DEVICE: (patientId: string) => `/patients/${patientId}/v2/match-device`,
    ADD_DEVICE: (patientId: string) => `/patients/${patientId}/v2/add-device`,
    DIAGNOSES: (patientId: string) => `/patients/${patientId}/diagnoses`,
    INSURANCES: (patientId: string) => `/patients/${patientId}/insurances`,
    TRANSMISSION_REPORTS: (patientId: string) => `/patients/v2/${patientId}/transmission-reports`,
    UPDATE_PATIENT: (patientId: string) => `/patients/${patientId}`
  },
  PROGRAMMED_PARAMETERS: {
    UPDATE: (reportId: number) => `/transmission-reports/${reportId}/programmedParameters`
  },
  REPORT_TEMPLATE: {
    LIST: (reportId: number) => `/report-template/${reportId}`,
    LIST_V2: (reportId: number) => `/report-template/v2/mappings/${reportId}`,
    APPLY: '/report-template/apply',
    REMOVE: '/report-template/remove',
    REPLACE: '/report-template/replace',
    ORGANIZATION_LIST: (organizationId: string) => `/physician-interpretation/v2/${organizationId}`
  },
  TRANSMISSION_REPORTS: {
    ROOT: '/transmission-reports',
    LIST: '/transmission-reports/list/paginated',
    COUNT: '/transmission-reports/count',
    FILTERED_COUNT: '/transmission-reports/filtered-count',
    DISMISS: (reportId: number) => `/transmission-reports/${reportId}/dismiss`,
    DISMISS_MULTIPLE: (reportIds: number[]) =>
      `/transmission-reports/${reportIds.join('%2C')}/dismissAll`, // endpoint takes comma separated values which is encoded as %2C
    ICD_DIAGNOSIS_CODES: '/transmission-reports/icd-diagnosis-codes',
    VENDOR_REPORT_IMAGE: (reportId: number) =>
      `/transmission-reports/${reportId}/vendor-report-image`,
    VENDOR_REPORTS: (reportId: number) =>
      `/transmission-reports/${reportId}/vendor-transmissions-reports`,
    UPDATE_MULTIPLE_OVERRIDES: (reportId: number) =>
      `/transmission-reports/${reportId}/overrides/multiple`,
    SIGN: (reportId: number) => `/transmission-reports/${reportId}/sign`,
    ATTACHMENTS: (reportId: number) => `/transmission-reports/${reportId}/attachments`,
    SEARCH_UNSIGNED_REPORTS: '/transmission-reports/search-unsigned-reports',
    STORE_PDF_DOWNLOAD_INFO: (txId: number, downloadDate: string) =>
      `/transmission-reports/${txId}/${downloadDate}/store`,
    INCLINIC_EPISODE: (reportId: number) => `/transmission-reports/${reportId}/inClinicEpisode`,
    UNDISMISS: (reportId: number) => `/transmission-reports/${reportId}/unDismiss`,
    UPLOAD_IN_CLINIC_TEMP_PDF: (patientId: string) =>
      `/transmission-reports/${patientId}/upload-inclinicpdf`,
    SAVE_MANUAL_IN_CLINIC_REPORT: `/transmission-reports/manual-inclinic-reports/save`,
    EDIT_SIGNED_REPORT: (reportId: number, isEditable: boolean) =>
      `/transmission-reports/${reportId}/editReport/${isEditable}`,
    LEAD_VENDOR_LIST: '/transmission-reports/lead-vendor/all',
    NAVIGATION: (reportId: number) => `/transmission-reports/${reportId}/navigation`,
    GET_PATIENT_REPORTS: (patientId: string) =>
      `/transmission-reports/v2/${patientId}/patient-reports`,
    APPLY_PHYSICIAN_TEMPLATE: (reportId: number) =>
      `/transmission-reports/v2/${reportId}/physician-interpretation`,
    SEND_TO_PROVIDER: (id: number) => `/transmission-reports/${id}/send-to-provider`,
    CREATE_TRANSMISSION_REPORT: `/transmission-reports/v2/create-report`,
    CREATE_TRANSMISSION_INCLINIC_REPORT: `/transmission-reports/create-in-clinic-transmission-report`,
    ATTACHMENT_UPLOAD_URLS: (id: number) => `/transmission-reports/${id}/attachments/upload-url`,
    ATTACHMENT_SAVE: (id: number) => `/transmission-reports/${id}/attachments/save`,
    UPDATE_REPORT_TYPE: (reportId: number) =>
      `/transmission-reports/${reportId}/update-report-type`,
    START_REVIEW: (reportId: number) => `/transmission-reports/${reportId}/start-review`,
    HEARTBEAT: (reportId: number) => `/transmission-reports/${reportId}/heartbeat`,
    FINISH_REVIEW: (reportId: number) => `/transmission-reports/${reportId}/finish-review`
  },
  USERS: {
    CURRENT_USER: '/users/user/currentUserInfo',
    USER_SETTINGS: '/user-settings',
    UPDATE_GLOBAL_SETTINGS: (id: string) => `/user-settings/${id}/update-global-settings`,
    UPDATE_PAGE_PREFERENCES: (id: string) => `/user-settings/${id}/update-page-preferences`,
    PRACTITIONERS_BY_CLINIC_ID: '/users/practitioners'
  },
  BATCH_DOWNLOAD: {
    CREATE: '/batch-download',
    GET: (id: number) => `/batch-download/${id}`,
    UPDATE_BATCH_PDF_DOWNLOAD_INFO: '/batch-download/updateBatchPDFDownloadInfo',
    ALL: '/batch-download/getPatientTransmissionData'
  },
  SCHEDULE_TRANSMISSION: {
    LIST: '/scheduled-transmission',
    NEXT_SCHEDULES: (patientId: string) =>
      `/scheduled-transmission/${patientId}/find-upcoming-Scheduled-transmission`,
    RESET: '/scheduled-transmission/reset'
  },
  NPI: {
    GET: '/npi',
    TYPEAHEAD: '/npi/typeahead',
    TAXONOMY_CODES: '/npi/taxonomy'
  },
  FEATURE_FLAGS: {
    LIST: '/feature-flags',
    ADMIN: '/feature-flags/admin',
    UPDATE: '/feature-flags'
  },
  ANALYTICS: {
    ACCOUNT_OVERVIEW: '/analytics/account-overview',
    DEVICE_RECALLS: '/analytics/device-recalls',
    BATTERY_LIFE: '/analytics/battery-life',
    LEADS_RECALLS: '/analytics/lead-recalls',
    ALERTS: '/analytics/alerts',
    BILLINGS: '/analytics/billable',
    SIGNATURES: '/analytics/signature',
    DOWNLOAD: '/analytics/download-report'
  },
  ACTIVITY_FEED: {
    LIST: (patientId: string) => `/patients/${patientId}/activity`
  },
  PHYSICIAN_INTERPRETATION: {
    LIST: `/physician-interpretation/templates`
  },
  V2_PHYSICIAN_INTERPRETATION: {
    LIST: (reportId: number) => `/physician-interpretation/v2/templates/${reportId}`
  },
  APPOINTMENT: {
    LIST: `/appointments`,
    LINK_TRANSMISSION: `/appointments/link-transmission`,
    ACTIVE: (deviceId: string) => `/appointments/active?deviceId=${deviceId}`,
    RUN_SCHEDULER: '/run-scheduler',
    CANCEL_ALL: '/cancel-all'
  },
  PATIENT_DIAGNOSES: {
    UPDATE_PRIMARY: (diagnosisId: string) => `/patient-diagnoses/${diagnosisId}`,
    CLEAR_ALL: (patientId: string) => `/patient-diagnoses/${patientId}/clear`
  },
  DEVICE_RECALL: {
    GET: (deviceId: string) => `/device-recall/${deviceId}`,
    GET_LEADS: (deviceId: string) => `/leads-recall/${deviceId}`
  },
  BOX_IN_CLINIC: {
    LIST: '/box/get-unassigned-files',
    SEARCH_PATIENTS: '/box/search-patients',
    CREATE_REPORT: '/transmission-reports/v2/create-report',
    DELETE_FILE: (fileId: string) => `box/delete-file/${fileId}`,
    DOWNLOAD_FILE: (fileId: string) => `/box/download/${fileId}`
  }
};

export enum API_SLICE {
  Patient = 'Patient',
  PatientList = 'PatientList',
  PatientDocuments = 'PatientDocuments',
  PatientRecalls = 'PatientRecalls',
  Schedules = 'Schedules',
  TransmissionReport = 'TransmissionReport',
  LeadChambers = 'LeadChambers',
  File = 'File',
  Billing = 'Billing',
  User = 'User',
  Organization = 'Organization',
  ClinicalNotes = 'ClinicalNotes',
  BatchDownload = 'BatchDownload',
  InClinicPractitioners = 'InClinicPractitioners',
  NPI = 'NPI',
  XifinOrderingPhysician = 'XifinOrderingPhysician',
  FeatureFlags = 'FeatureFlags',
  Analytics = 'Analytics',
  DiagnosisCodes = 'DiagnosisCodes',
  ActivityFeed = 'ActivityFeed',
  PhysicianInterpretation = 'PhysicianInterpretation',
  Appointment = 'Appointment',
  ScheduleList = 'ScheduleList',
  PatientDiagnoses = 'PatientDiagnoses',
  Insurance = 'Insurance',
  BoxInClinic = 'BoxInClinic'
}

export const BASE_URL = import.meta.env.VITE_APP_API_URL ?? 'http://localhost:8001';
